import {
  ArrowDownTrayIcon,
  CalendarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { apiService, handleApiError } from "../services/api";

export default function Reports() {
  // Real API call to get reports
  const {
    data: reportsData,
    error: reportsError,
    isLoading,
  } = useQuery({
    queryKey: ["reports"],
    queryFn: apiService.getReports,
    refetchInterval: 30000,
    retry: 2,
  });

  const reports = reportsData?.reports || [];

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getReportTypeIcon = (_type: string) => {
    return <DocumentTextIcon className="h-5 w-5 text-gray-400" />;
  };

  const getReportTypeBadge = (type: string) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (type) {
      case "PDF":
        return `${baseClasses} bg-red-100 text-red-800`;
      case "HTML":
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      const blob = await apiService.downloadReport(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download report: " + handleApiError(error));
    }
  };

  const extractTickersFromFilename = (filename: string): string[] => {
    // Extract tickers from filename pattern like "executive_summary_AAPL_MSFT_20240115.pdf"
    const parts = filename.split("_");
    const tickers = [];
    for (let i = 2; i < parts.length - 1; i++) {
      if (parts[i].match(/^[A-Z]{1,5}$/)) {
        tickers.push(parts[i]);
      }
    }
    return tickers.length > 0 ? tickers : ["N/A"];
  };

  const getAnalysisTypeFromFilename = (filename: string): string => {
    if (filename.includes("executive_summary")) return "executive";
    if (filename.includes("detailed_analysis")) return "detailed";
    if (filename.includes("risk_assessment")) return "risk";
    return "standard";
  };

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {reportsError && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-danger-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-danger-800">
                Unable to Load Reports
              </h4>
              <p className="mt-2 text-sm text-danger-700">
                {handleApiError(reportsError)}
              </p>
              <p className="text-xs mt-1">
                Make sure the Flask backend is running on port 8000
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            Generated financial analysis reports and documents
          </p>
        </div>
        <div className="flex space-x-3">
          <button className="btn-secondary">
            <CalendarIcon className="h-4 w-4 mr-2" />
            Filter by Date
          </button>
          <button className="btn-primary">Generate New Report</button>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 gap-6">
        {isLoading ? (
          <div className="card">
            <div className="text-center py-12">
              <div className="spinner w-8 h-8 mx-auto mb-4" />
              <p className="text-gray-600">Loading reports...</p>
            </div>
          </div>
        ) : reports.length > 0 ? (
          <div className="card">
            <div className="px-4 py-5 sm:p-6">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="text-left">Report</th>
                      <th className="text-left">Type</th>
                      <th className="text-left">Tickers</th>
                      <th className="text-left">Analysis</th>
                      <th className="text-left">Size</th>
                      <th className="text-left">Modified</th>
                      <th className="text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reports.map((report, index) => (
                      <tr
                        key={report.filename || index}
                        className="hover:bg-gray-50"
                      >
                        <td className="py-4">
                          <div className="flex items-center">
                            {getReportTypeIcon(report.type)}
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {report.filename}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4">
                          <span className={getReportTypeBadge(report.type)}>
                            {report.type}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex space-x-1">
                            {extractTickersFromFilename(report.filename).map(
                              (ticker) => (
                                <span
                                  key={ticker}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {ticker}
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="py-4">
                          <span className="text-sm text-gray-600 capitalize">
                            {getAnalysisTypeFromFilename(report.filename)}
                          </span>
                        </td>
                        <td className="py-4">
                          <span className="text-sm text-gray-600">
                            {formatFileSize(report.size)}
                          </span>
                        </td>
                        <td className="py-4">
                          <span className="text-sm text-gray-600">
                            {formatDate(report.modified)}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() =>
                                window.open(report.download_url, "_blank")
                              }
                              className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <EyeIcon className="h-3 w-3 mr-1" />
                              View
                            </button>
                            <button
                              onClick={() => handleDownload(report.filename)}
                              className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No reports found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {reportsError
                  ? "Unable to load reports from the backend."
                  : "Get started by running your first financial analysis."}
              </p>
              <div className="mt-6">
                <button className="btn-primary">Run Analysis</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Report Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Reports</p>
              <p className="text-2xl font-semibold text-gray-900">
                {isLoading ? (
                  <div className="spinner w-6 h-6" />
                ) : (
                  reports.length
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">This Month</p>
              <p className="text-2xl font-semibold text-gray-900">
                {isLoading ? (
                  <div className="spinner w-6 h-6" />
                ) : (
                  reports.filter(
                    (r) =>
                      new Date(r.modified).getMonth() === new Date().getMonth()
                  ).length
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowDownTrayIcon className="h-8 w-8 text-success-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Size</p>
              <p className="text-2xl font-semibold text-gray-900">
                {isLoading ? (
                  <div className="spinner w-6 h-6" />
                ) : (
                  formatFileSize(
                    reports.reduce((acc, report) => acc + report.size, 0)
                  )
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
